import React, { useState, useEffect } from 'react';
import { collection, getDocs, getDoc, query, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  Container,
  Grid2 as Grid,
  Fade,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../AuthContext';

// Modern color palette (consistent with PastPapers.js)
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  background: '#f5f7fa',
  accent: '#3498db',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

// Styled components
const ModernCard = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  background: 'white',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(2),
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1.25, 2.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: colors.accent,
    color: 'white',
    '& svg': {
      transform: 'translateX(6px)',
    },
  },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1, 2),
  color: '#e74c3c',
  borderColor: '#e74c3c',
  '&:hover': {
    backgroundColor: '#e74c3c',
    color: 'white',
    borderColor: '#e74c3c',
  },
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 450 },
  maxWidth: '100%',
  backgroundColor: 'white',
  borderRadius: { xs: '8px', sm: '12px' },
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
  padding: { xs: theme.spacing(2), sm: theme.spacing(4) },
  maxHeight: '90vh',
  overflowY: 'auto',
}));

const RevisionHub = () => {
  const [revisionPapers, setRevisionPapers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedRevisionPaper, setSelectedRevisionPaper] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const navigate = useNavigate();
  const { user } = useAuth();

  // Access status options
  const accessStatusOptions = [
    { value: 'trial', label: 'Trial' },
    { value: 'premium', label: 'Premium' },
  ];

  // Check if user is admin
  useEffect(() => {
    if (!user) {
      navigate('/sign-in');
    } else {
      const checkAdmin = async () => {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists() && userDoc.data().role !== 'admin') {
          navigate('/dashboard');
        }
      };
      checkAdmin().catch((error) => {
        console.error('Error checking admin status:', error);
        navigate('/sign-in');
      });
    }
  }, [user, navigate]);

  // Fetch revision papers
  useEffect(() => {
    const fetchRevisionPapers = async () => {
      setIsLoading(true);
      try {
        const revisionPapersCollection = collection(db, 'revisionPapers');
        const revisionPapersSnapshot = await getDocs(query(revisionPapersCollection));
        const revisionPapersData = revisionPapersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          quizUrl: doc.data().quizUrl || doc.data().url || 'N/A',
          leaderboardUrl: doc.data().leaderboardUrl || doc.data().solutionsUrl || 'N/A',
          accessStatus: doc.data().accessStatus || 'trial',
          category: doc.data().category || '',
        }));
        setRevisionPapers(revisionPapersData);
      } catch (error) {
        console.error('Error fetching revision papers:', { message: error.message, code: error.code });
        setSnackbarMessage('Error fetching revision papers');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRevisionPapers();
  }, []);

  // Filter revision papers
  const filteredRevisionPapers = revisionPapers
    .filter((paper) => {
      const matchesText =
        paper.title.toLowerCase().includes(searchText.toLowerCase()) ||
        paper.description.toLowerCase().includes(searchText.toLowerCase());
      return matchesText;
    })
    .sort((a, b) => a.title.localeCompare(b.title));

  // Handle edit click
  const handleEditClick = (paper) => {
    setSelectedRevisionPaper(paper);
    setOpenModal(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRevisionPaper(null);
  };

  // Handle Snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // Validate URL format
  const isValidUrl = (url) => {
    if (!url || url === 'N/A') return true; // Allow empty or N/A URLs
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Handle save revision paper
  const handleSaveRevisionPaper = async () => {
    if (!selectedRevisionPaper) return;

    const currentYear = new Date().getFullYear();
    if (
      !selectedRevisionPaper.title.trim() ||
      !selectedRevisionPaper.description.trim() ||
      !selectedRevisionPaper.year ||
      isNaN(selectedRevisionPaper.year) ||
      selectedRevisionPaper.year < 1900 ||
      selectedRevisionPaper.year > currentYear ||
      !selectedRevisionPaper.subject.trim() ||
      !selectedRevisionPaper.category.trim() ||
      !selectedRevisionPaper.schoolLevel.trim() ||
      !selectedRevisionPaper.quizUrl.trim() ||
      !isValidUrl(selectedRevisionPaper.quizUrl) ||
      (selectedRevisionPaper.leaderboardUrl && selectedRevisionPaper.leaderboardUrl !== 'N/A' && !isValidUrl(selectedRevisionPaper.leaderboardUrl)) ||
      !selectedRevisionPaper.accessStatus
    ) {
      setSnackbarMessage('Please fill all required fields with valid data.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const paperRef = doc(db, 'revisionPapers', selectedRevisionPaper.id);
      await updateDoc(paperRef, {
        title: selectedRevisionPaper.title.trim(),
        description: selectedRevisionPaper.description.trim(),
        year: parseInt(selectedRevisionPaper.year, 10),
        subject: selectedRevisionPaper.subject.trim(),
        category: selectedRevisionPaper.category.trim(),
        schoolLevel: selectedRevisionPaper.schoolLevel.trim(),
        quizUrl: selectedRevisionPaper.quizUrl.trim(),
        leaderboardUrl: selectedRevisionPaper.leaderboardUrl ? selectedRevisionPaper.leaderboardUrl.trim() : '',
        accessStatus: selectedRevisionPaper.accessStatus,
      });
      setRevisionPapers(
        revisionPapers.map((paper) =>
          paper.id === selectedRevisionPaper.id ? { ...selectedRevisionPaper, leaderboardUrl: selectedRevisionPaper.leaderboardUrl || 'N/A' } : paper
        )
      );
      handleCloseModal();
      setSnackbarMessage('Revision paper updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating revision paper:', { message: error.message, code: error.code });
      setSnackbarMessage('Failed to update revision paper.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Handle delete revision paper
  const handleDeleteRevisionPaper = async (paperId) => {
    if (window.confirm('Are you sure you want to delete this revision paper? This action cannot be undone.')) {
      try {
        const paperRef = doc(db, 'revisionPapers', paperId);
        await deleteDoc(paperRef);
        setRevisionPapers(revisionPapers.filter((paper) => paper.id !== paperId));
        setSnackbarMessage('Revision paper deleted successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Error deleting revision paper:', { message: error.message, code: error.code });
        setSnackbarMessage('Failed to delete revision paper.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  // Handle input change in edit modal
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedRevisionPaper((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // DataGrid columns
  const columns = [
    { field: 'title', headerName: 'Title', width: 200 },
    { field: 'description', headerName: 'Description', width: 200 },
    { field: 'year', headerName: 'Year', width: 100 },
    { field: 'subject', headerName: 'Subject', width: 150 },
    { field: 'category', headerName: 'Category', width: 150 },
    { field: 'schoolLevel', headerName: 'School Level', width: 150 },
    {
      field: 'accessStatus',
      headerName: 'Access Status',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ color: params.value === 'premium' ? colors.accent : colors.primary, fontWeight: '600' }}>
          {params.value.charAt(0).toUpperCase() + params.value.slice(1)}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 400,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <AnimatedButton
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleEditClick(params.row)}
          >
            Edit
          </AnimatedButton>
          <AnimatedButton
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => {
              if (params.row.quizUrl && params.row.quizUrl !== 'N/A') {
                window.open(params.row.quizUrl, '_blank', 'noopener,noreferrer');
              } else {
                setSnackbarMessage('No quiz URL available for this revision paper.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
              }
            }}
          >
            View Quiz
          </AnimatedButton>
          <AnimatedButton
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => {
              if (params.row.leaderboardUrl && params.row.leaderboardUrl !== 'N/A') {
                window.open(params.row.leaderboardUrl, '_blank', 'noopener,noreferrer');
              } else {
                setSnackbarMessage('No leaderboard URL available for this revision paper.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
              }
            }}
          >
            View Leaderboard
          </AnimatedButton>
          <DeleteButton
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteRevisionPaper(params.row.id)}
          >
            Delete
          </DeleteButton>
        </Box>
      ),
    },
  ];

  return (
    <div style={{ background: colors.background }}>
      <Container sx={{ py: 8, minHeight: '100vh', mt: 5 }}>
        {/* Header */}
        <Fade in timeout={800}>
          <Box
            sx={{
              mb: 5,
              p: 4,
              borderRadius: '16px',
              background: `linear-gradient(159deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
              color: 'white',
              boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: '700', display: 'flex', alignItems: 'center' }}>
              <AutoStoriesIcon sx={{ fontSize: 35, mr: 1 }} />
              Revision Hub
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
              Manage and edit revision papers in the Maths Gem Seychelles portal.
            </Typography>
          </Box>
        </Fade>

        {/* Filters and Add Button */}
        <ModernCard>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: colors.secondary, fontWeight: '600' }}>
                Revision Paper List
              </Typography>
              <AnimatedButton
                variant="contained"
                color="primary"
                onClick={() => navigate('/addrevisionpapers')}
                startIcon={<AddIcon />}
              >
                Add New Revision Paper
              </AnimatedButton>
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 12 }}>
                <TextField
                  fullWidth
                  label="Search Revision Papers"
                  variant="outlined"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search by title or description"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      '& fieldset': { borderColor: colors.primary },
                      '&:hover fieldset': { borderColor: colors.accent },
                      '&.Mui-focused fieldset': { borderColor: colors.accent },
                    },
                    '& .MuiInputLabel-root': {
                      color: colors.secondary,
                      '&.Mui-focused': { color: colors.accent },
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* DataGrid */}
            <Box sx={{ mt: 3 }}>
              <DataGrid
                rows={filteredRevisionPapers}
                columns={columns}
                pageSize={10}
                checkboxSelection={false}
                loading={isLoading}
                initialState={{
                  sorting: { sortModel: [{ field: 'title', sort: 'asc' }] },
                }}
                sx={{
                  '& .MuiDataGrid-root': { border: 'none' },
                  '& .MuiDataGrid-cell': { color: colors.secondary },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: colors.background,
                    color: colors.secondary,
                    fontWeight: '600',
                  },
                  '& .MuiDataGrid-row:hover': { backgroundColor: '#f0f4f8' },
                }}
              />
            </Box>
          </Box>
        </ModernCard>

        {/* Edit Modal */}
        <Modal open={openModal} onClose={handleCloseModal}>
          <ModalBox sx={{ padding: 3, borderRadius: 5 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: '700', color: colors.secondary, mb: 3 }}
            >
              Edit Revision Paper
            </Typography>
            {selectedRevisionPaper && (
              <Box component="form">
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={selectedRevisionPaper.title}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={selectedRevisionPaper.description}
                  onChange={handleInputChange}
                  multiline
                  rows={4}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Year"
                  name="year"
                  value={selectedRevisionPaper.year}
                  onChange={handleInputChange}
                  type="number"
                  inputProps={{ min: 1900, max: new Date().getFullYear() }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Subject"
                  name="subject"
                  value={selectedRevisionPaper.subject}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Revision Paper Category"
                  name="category"
                  value={selectedRevisionPaper.category}
                  onChange={handleInputChange}
                  placeholder="e.g. Mock Exams, Final Exams, End of Term"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="School Level"
                  name="schoolLevel"
                  value={selectedRevisionPaper.schoolLevel}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Quiz URL"
                  name="quizUrl"
                  value={selectedRevisionPaper.quizUrl}
                  onChange={handleInputChange}
                  type="url"
                  placeholder="https://example.com/quiz"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Leaderboard URL"
                  name="leaderboardUrl"
                  value={selectedRevisionPaper.leaderboardUrl}
                  onChange={handleInputChange}
                  type="url"
                  placeholder="https://example.com/leaderboard"
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel sx={{ color: colors.secondary }}>Access Status</InputLabel>
                  <Select
                    name="accessStatus"
                    value={selectedRevisionPaper.accessStatus}
                    onChange={handleInputChange}
                    label="Access Status"
                    sx={{
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                    }}
                  >
                    {accessStatusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <AnimatedButton
                    variant="contained"
                    color="primary"
                    onClick={handleSaveRevisionPaper}
                  >
                    Save
                  </AnimatedButton>
                  <AnimatedButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </AnimatedButton>
                </Box>
              </Box>
            )}
          </ModalBox>
        </Modal>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: '100%', bgcolor: snackbarSeverity === 'success' ? colors.primary : '#e74c3c', color: 'white' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};

export default RevisionHub;
